/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { InsightLinkCardGroup } from '@atlassian/rovo-platform-ui-components/insight-link-card-group';

import { ProfileAvatarGroup } from '../../../../common/ui/profile-avatar-group';

import type { InsightSubItem } from './types';

const styles = cssMap({
	// Base card styles — background is transparent so the outer positionStyles wrapper
	// controls the full-height hover/active highlight (including the artifact section below).
	// Border radius is omitted here since the outer wrapper's overflow:hidden + radius.xlarge
	// clips the content — no need to duplicate it on this element.
	card: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.0'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		cursor: 'pointer',
		gap: token('space.100'),
		backgroundColor: 'transparent',
	},
	textSection: {
		display: 'flex',
		flexDirection: 'column',
		maxWidth: '579px',
		flexGrow: 1,
		// overflow:hidden + minWidth:0 ensure the flex child can shrink below its content size,
		// which is required for text-overflow:ellipsis to work on children.
		overflow: 'hidden',
		minWidth: '0px',
		textAlign: 'left',
	},
	// Bottom section below the artifact link cards — holds the avatar group.
	// In sidebar mode: column layout (avatars below artifact).
	// In fullscreen mode: row layout with space-between (avatars to the right of artifact).
	bottomSection: {
		display: 'flex',
		alignItems: 'center',
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
	},
	bottomSectionWithoutLinks: {
		display: 'flex',
		alignItems: 'center',
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.200'),
	},
	bottomSectionFullScreen: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		gap: token('space.100'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
		overflow: 'hidden',
	},
	linkGroupFullScreen: {
		position: 'relative',
		pointerEvents: 'none',
		minWidth: '0px',
		maxWidth: '100%',
		flex: '0 1 auto',
		overflow: 'hidden',
	},
	avatarContainerFullScreen: {
		minWidth: '0px',
		maxWidth: '100%',
		flexShrink: 0,
		alignSelf: 'flex-end',
	},
	avatarContainerFullScreenWithSpacing: {
		minWidth: '0px',
		maxWidth: '100%',
		flexShrink: 0,
		alignSelf: 'flex-end',
		paddingBlockStart: token('space.100'),
	},
});

// Cards live inside a shared bordered+rounded container (overflow:hidden clips corners).
// No individual card borders — rely on group-level border.
// overflow:hidden on ALL positions ensures content clips to the rounded shape —
// child elements (e.g. Pressable) do NOT need their own borderRadius.
// Hover/active/cursor styles live here so the entire card area — including the
// link-cards section below the Pressable — highlights on mouse interaction.
// position:relative anchors the Pressable's absolute click-overlay to the whole card.
const positionStyles = unboundedCssMap({
	only: {
		overflow: 'hidden',
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest'),
		},
	},
	first: {
		overflow: 'hidden',
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest'),
		},
	},
	middle: {
		overflow: 'hidden',
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest'),
		},
	},
	last: {
		overflow: 'hidden',
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest'),
		},
	},
});

// Vendor-prefixed styles — use @compiled/react cssMap (unbounded)
// because @atlaskit/css cssMap rejects vendor-prefixed props.
const vendorStyles = unboundedCssMap({
	titleClampDefault: {
		overflow: 'hidden',
		display: '-webkit-box',
		WebkitLineClamp: 3,
		WebkitBoxOrient: 'vertical',
		font: token('font.body'),
		color: token('color.text'),
	},
	titleClampFullScreen: {
		overflow: 'hidden',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		font: token('font.body'),
		color: token('color.text'),
	},
	titleWeight: {
		fontWeight: token('font.weight.medium'),
	},
	meetingTitlePrimaryDefaultLines: {
		WebkitLineClamp: 2,
	},
	meetingTitlePrimaryFullScreenLines: {
		WebkitLineClamp: 1,
	},
	meetingTitleSecondary: {
		overflow: 'hidden',
		display: 'block',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		font: token('font.body'),
		color: token('color.text'),
	},
	descriptionClamp: {
		display: '-webkit-box',
		width: '100%',
		margin: 0,
		overflow: 'hidden',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		textOverflow: 'ellipsis',
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
	// Bottom delimiter rendered as a separate full-width div inside the overflow:hidden
	// wrapper, so it is not clipped. Placed after link cards as the last element.
	delimiter: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	// Stretches the title Pressable's hit area over the whole card (anchored to the
	// position:relative wrapper) so any click activates that one button.
	clickOverlay: {
		position: 'absolute',
		insetBlockStart: token('space.0', '0px'),
		insetInlineStart: token('space.0', '0px'),
		insetBlockEnd: token('space.0', '0px'),
		insetInlineEnd: token('space.0', '0px'),
	},
	// pointer-events:none lets padding clicks fall through to the overlay; linkInteractive
	// re-enables them on the link cards so those stay independently clickable.
	linkGroupWrapper: {
		position: 'relative',
		pointerEvents: 'none',
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.150'),
	},
	linkGroupWrapperWithoutPeople: {
		position: 'relative',
		pointerEvents: 'none',
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
	},
	linkInteractive: {
		pointerEvents: 'auto',
	},
	// Rendered as a sibling spacer right after the Pressable when there are no link
	// cards below it, so the card still has consistent bottom spacing.
	bottomPadding: {
		paddingBlockEnd: token('space.200'),
	},
});

/** Controls border-radius and border per card position within a group list. */
export type GenericInsightCellPosition = 'only' | 'first' | 'middle' | 'last';

export interface GenericInsightCellProps {
	/** The insight sub-item data to render */
	item: InsightSubItem;
	/** Called when the card is pressed, with the insight's id */
	onInsightPress: (insightId: string) => void;
	/**
	 * Position of this card within the group list — controls border-radius and which
	 * borders are shown to form a seamless grouped visual.
	 * Defaults to 'only' (all corners rounded, full border).
	 */
	position?: GenericInsightCellPosition;
	/** Whether the cell is rendered in fullscreen layout. */
	isFullScreen?: boolean;
	/** Optional callback invoked when a link card within the cell is clicked. */
	onLinkClick?: (url: string) => void;
}

/**
 * GenericInsightCell — Renders a single insight item within an expanded InsightsFeedGroup.
 * Each card owns its own 1px border and selective border-radius based on `position`:
 *   'only'   → all 4 corners rounded, full border
 *   'first'  → top corners rounded, no bottom border
 *   'middle' → no rounded corners, no top border (left/right/bottom borders remain as divider)
 *   'last'   → bottom corners rounded, no top border
 */
export const GenericInsightCell = ({
	item,
	onInsightPress,
	position = 'only',
	isFullScreen = false,
	onLinkClick,
}: GenericInsightCellProps): JSX.Element => {
	const handleClick = useCallback(() => {
		onInsightPress(item.id);
	}, [onInsightPress, item.id]);

	const hasDelimiter = position === 'first' || position === 'middle';

	const urlList = item.urls && item.urls.length > 0 ? item.urls : null;

	const hasPeople = item.people && item.people.length > 0;

	const avatarElement = hasPeople ? (
		<ProfileAvatarGroup
			people={item.people!}
			testId="insight-card-avatars"
			namesFirst={isFullScreen}
		/>
	) : null;
	const linkCardGroupElement = urlList ? (
		<div css={vendorStyles.linkInteractive}>
			<InsightLinkCardGroup urls={urlList} isFullScreen={isFullScreen} onLinkClick={onLinkClick} />
		</div>
	) : null;

	return (
		<div css={positionStyles[position]} data-testid={`generic-insight-cell-${item.id}`}>
			<Pressable xcss={styles.card} onClick={handleClick}>
				<span aria-hidden="true" css={vendorStyles.clickOverlay} />
				<Flex
					xcss={styles.textSection}
					direction="column"
					gap={isFullScreen ? 'space.150' : 'space.100'}
				>
					{item.titleSecondary ? (
						<div>
							<div
								css={[
									isFullScreen ? vendorStyles.titleClampFullScreen : vendorStyles.titleClampDefault,
									vendorStyles.titleWeight,
									isFullScreen
										? vendorStyles.meetingTitlePrimaryFullScreenLines
										: vendorStyles.meetingTitlePrimaryDefaultLines,
								]}
							>
								{item.title}
							</div>
							{/* titleSecondary is parsed from backend-provided meeting title display text. */}
							<div css={[vendorStyles.meetingTitleSecondary, vendorStyles.titleWeight]}>
								{item.titleSecondary}
							</div>
						</div>
					) : (
						<div
							css={[
								isFullScreen ? vendorStyles.titleClampFullScreen : vendorStyles.titleClampDefault,
								vendorStyles.titleWeight,
							]}
						>
							{item.title}
						</div>
					)}
					<span css={vendorStyles.descriptionClamp}>{item.description}</span>
				</Flex>
			</Pressable>
			{isFullScreen ? (
				// Fullscreen: artifact link cards and avatar group on the same row, spaced apart
				urlList || avatarElement ? (
					<Flex
						xcss={styles.bottomSectionFullScreen}
						justifyContent={
							urlList && avatarElement ? 'space-between' : avatarElement ? 'end' : 'start'
						}
					>
						{urlList && <Box xcss={styles.linkGroupFullScreen}>{linkCardGroupElement}</Box>}
						{avatarElement ? (
							<Box
								xcss={
									urlList
										? styles.avatarContainerFullScreen
										: styles.avatarContainerFullScreenWithSpacing
								}
							>
								{avatarElement}
							</Box>
						) : null}
					</Flex>
				) : (
					<div css={vendorStyles.bottomPadding} />
				)
			) : (
				// Sidebar: artifact link cards above, avatar group below
				<React.Fragment>
					{urlList && avatarElement ? (
						<div css={vendorStyles.linkGroupWrapper}>{linkCardGroupElement}</div>
					) : null}
					{urlList && !avatarElement ? (
						<div css={vendorStyles.linkGroupWrapperWithoutPeople}>{linkCardGroupElement}</div>
					) : null}
					{avatarElement ? (
						<Flex xcss={urlList ? styles.bottomSection : styles.bottomSectionWithoutLinks}>
							{avatarElement}
						</Flex>
					) : null}
					{!urlList && !avatarElement && <div css={vendorStyles.bottomPadding} />}
				</React.Fragment>
			)}
			{hasDelimiter && <div css={vendorStyles.delimiter} />}
		</div>
	);
};
